import { isRightMBClick } from '@lessonup/ui-components';
import { useCallback, useEffect, useRef } from 'react';
import { OnMouseDownHandler } from '../../../hooks/useHandleMouseEvents/useHandleMouseEvents.types';

export interface CategoryAreaHandlerProps {
  componentId: string;
  active: boolean;
  editable: boolean;
  activeCategoryId?: string;
  isTextEditMode: boolean;
  layoutMode: 'AUTO' | 'CUSTOM';
  onMouseDown?: OnMouseDownHandler;
  setModeCategoryEdit?: (componentId: string, categoryId: string) => void;
  setModeTextEdit?: (componentId: string, categoryId: string) => void;
}

export function useCategoryAreaHandlers(props: CategoryAreaHandlerProps) {
  const {
    componentId,
    active,
    editable,
    activeCategoryId,
    isTextEditMode,
    layoutMode,
    onMouseDown,
    setModeCategoryEdit,
  } = props;

  const clickState = useRef<
    Map<
      string,
      {
        lastClickedCategoryId: string | null;
        clickCount: number;
        wasActiveBeforeClick: boolean;
        shouldTryForDetailMode: boolean;
        dragged: boolean;
      }
    >
  >(new Map());

  const getClickState = useCallback((categoryId: string) => {
    if (!clickState.current.has(categoryId)) {
      clickState.current.set(categoryId, {
        lastClickedCategoryId: null,
        clickCount: 0,
        wasActiveBeforeClick: false,
        shouldTryForDetailMode: false,
        dragged: false,
      });
    }
    return clickState.current.get(categoryId)!;
  }, []);

  const handleCategoryMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, categoryId: string) => {
      if (isTextEditMode && activeCategoryId === categoryId) {
        return;
      }

      if (layoutMode !== 'CUSTOM' || !active || !getClickState(categoryId).shouldTryForDetailMode) return;

      getClickState(categoryId).dragged = true;
    },
    [active, layoutMode, getClickState, isTextEditMode, activeCategoryId]
  );

  const handleCategoryMouseUp = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, categoryId: string) => {
      if (!editable) return;

      if (isTextEditMode && activeCategoryId === categoryId) {
        return;
      }

      const clickState = getClickState(categoryId);
      if (!clickState.wasActiveBeforeClick) {
        clickState.lastClickedCategoryId = null;
        clickState.clickCount = 0;
        clickState.shouldTryForDetailMode = false;
        return;
      }

      if (clickState.dragged) {
        clickState.dragged = false;
        clickState.shouldTryForDetailMode = false;
        return;
      }

      clickState.shouldTryForDetailMode = false;

      if (clickState.lastClickedCategoryId === categoryId) {
        clickState.clickCount++;
      } else {
        clickState.lastClickedCategoryId = categoryId;
        clickState.clickCount = 1;
      }

      if (clickState.clickCount === 1) {
        setModeCategoryEdit?.(componentId, categoryId);
        clickState.clickCount = 0;
      }
    },
    [editable, setModeCategoryEdit, componentId, getClickState, isTextEditMode, activeCategoryId]
  );

  const handleCategoryMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, categoryId: string) => {
      if (!editable) return;

      if (isRightMBClick(event)) {
        if (isTextEditMode && activeCategoryId === categoryId) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }

      if (isTextEditMode && activeCategoryId === categoryId) {
        return;
      }

      const clickState = getClickState(categoryId);
      clickState.wasActiveBeforeClick = active;
      clickState.dragged = false;

      if (!active) return;

      const isSecondClickOnSameCategory = activeCategoryId === categoryId;
      if (!isSecondClickOnSameCategory) {
        event.stopPropagation();
      }

      if (layoutMode === 'CUSTOM') {
        if (active && !isTextEditMode && activeCategoryId === categoryId) {
          clickState.shouldTryForDetailMode = true;
        }

        if (active && !isTextEditMode) {
          onMouseDown?.(event, { action: 'move-category', componentId, categoryId });
        }
      } else {
        clickState.shouldTryForDetailMode = false;
      }
    },
    [editable, active, layoutMode, onMouseDown, componentId, activeCategoryId, isTextEditMode, getClickState]
  );

  const handleCategoryContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, categoryId: string) => {
      if (!isTextEditMode || activeCategoryId !== categoryId) return;
      event.stopPropagation();
    },
    [isTextEditMode, activeCategoryId]
  );

  useEffect(() => {
    if (!active) {
      clickState.current.forEach((state) => {
        state.lastClickedCategoryId = null;
        state.clickCount = 0;
        state.wasActiveBeforeClick = false;
        state.shouldTryForDetailMode = false;
        state.dragged = false;
      });
    }
  }, [active]);

  return {
    handleCategoryMouseDown,
    handleCategoryMouseMove,
    handleCategoryMouseUp,
    handleCategoryContextMenu,
  };
}
